import { useCallback, useEffect, useRef, useState } from 'react';
import { VisualViewportContainerProps } from './types';

export const useVisualViewportContainer = (props: VisualViewportContainerProps) => {
  const { autoUpdate, autoUpdateInterval = 600, offsetHeight, ...otherProps } = props;
  const [viewportHeight, setViewportHeight] = useState(window.visualViewport?.height ?? 0);
  const updateViewportHeightRef = useRef(0);

  const getViewportHeight = useCallback(() => {
    const newVisualViewportHeight = window.visualViewport?.height;
    if (viewportHeight !== newVisualViewportHeight) {
      setViewportHeight(newVisualViewportHeight ?? 0);
    }
  }, [viewportHeight]);

  useEffect(() => {
    if (autoUpdate && !updateViewportHeightRef.current) {
      // eslint-disable-next-line functional/immutable-data -- Setting interval to auto resize.
      updateViewportHeightRef.current = window.setInterval(getViewportHeight, autoUpdateInterval);
    } else if (!autoUpdate && updateViewportHeightRef.current) {
      clearInterval(updateViewportHeightRef.current);
      // eslint-disable-next-line functional/immutable-data -- clear interval.
      updateViewportHeightRef.current = 0;
    }
  }, [autoUpdate, autoUpdateInterval, getViewportHeight]);

  useEffect(() => {
    window.visualViewport?.addEventListener('resize', getViewportHeight);
    window.visualViewport?.addEventListener('scroll', getViewportHeight);

    return () => {
      window.visualViewport?.removeEventListener('resize', getViewportHeight);
      window.visualViewport?.removeEventListener('scroll', getViewportHeight);
      clearInterval(updateViewportHeightRef.current);
    };
  });

  return {
    ...otherProps,
    style: {
      height: `100%`,
      maxHeight: offsetHeight ? `calc(${Math.round(viewportHeight) - offsetHeight}px)` : Math.round(viewportHeight),
    },
    viewportHeight,
  };
};
